import { toTestIdSegment } from "~/core";
import React, { useCallback } from "react";
import { useIntl } from "react-intl";
import { useStore } from "react-redux";
import { intlShape } from "@ai360/core/intl";

import classnames from "classnames";
import Immutable from "immutable";

import { model as accordionModel } from "~/accordion";
import {
    actions as cdActions,
    models as cdModels,
    selectors as cdSelectors,
} from "~/customer-data";
import { config as intlConfig } from "~/intl-provider/config";

import { FieldContextMenu } from "~/action-panel/components/context-menus/field-context-menu/field-context-menu";
import { selectors as fieldSelectors } from "~/action-panel/components/field-module";

import * as actions from "../../actions";
import { messages } from "../../i18n-messages";
import * as selectors from "../../selectors";
import { useAppDispatch, useAppSelector } from "~/store/hooks";

import "./field-item.css";
import { LockIcon } from "~/core/icons";

interface IFieldItemProps {
    customer: cdModels.CustomerInfo;
    field: cdModels.FieldInfo;
    intl: intlShape;
    itemDimIdx: accordionModel.MultiDimIdx;
    onAddEditField: (customer: cdModels.CustomerInfo, fieldGuid: string) => void;
    userRole: any;
}

const fieldItemPropsAreEqual = (prevProps: IFieldItemProps, nextProps: IFieldItemProps) =>
    prevProps.field === nextProps.field;

export const FieldItem = React.memo(function FieldItem_(props: IFieldItemProps) {
    const { customer, field, itemDimIdx, onAddEditField, userRole } = props;
    const { formatMessage, formatNumber } = useIntl();
    const store = useStore();

    const dispatch = useAppDispatch();
    const activeTab = useAppSelector(fieldSelectors.getActiveTab);
    const isSelected = useAppSelector((state) =>
        cdSelectors.getSelectedFieldGuids(state).has(field.fieldGuid)
    );

    const _onClick = useCallback(
        (event) => {
            if (event.isDefaultPrevented()) {
                return;
            }

            // itemList/lastClickedDimIdx are only needed here at click-time, not for
            // rendering, so they're read directly off the store instead of subscribed via
            // useAppSelector -- otherwise every field row would re-render on every click.
            const state = store.getState();
            const itemList = selectors.getAccordionItems(state);
            const lastClickedDimIdx = selectors.getLastClickedDimIdx(state);

            if (event.shiftKey) {
                const comp = accordionModel.dimIdxCompare(lastClickedDimIdx, itemDimIdx);
                if (comp === 0) {
                    return;
                }
                const fieldGuids = new Set<string>();
                const [startDimIdx, endDimIdx] =
                    comp === -1 ? [lastClickedDimIdx, itemDimIdx] : [itemDimIdx, lastClickedDimIdx];

                // It's unlikely, but possible for items in the list to be added/removed between
                // when `lastClickedDimIdx` was set and this click
                const invalidDimIdx = [startDimIdx, endDimIdx].filter(
                    (dimIdx) => !accordionModel.isValidDimIdx(itemList, dimIdx)
                );
                if (invalidDimIdx.length > 0) {
                    return;
                }
                for (const dimIdx of accordionModel.getDimIdxSliceIter(
                    itemList,
                    startDimIdx,
                    endDimIdx,
                    true,
                    [2]
                )) {
                    const { fieldGuid } = accordionModel.getItem(itemList, dimIdx).payload.field;
                    fieldGuids.add(fieldGuid);
                }
                dispatch(cdActions.addSelectedFields(Immutable.Set(fieldGuids)));
            } else if (isSelected) {
                dispatch(cdActions.clearSelectedField(field.fieldGuid));
            } else {
                dispatch(cdActions.addSelectedField(field.fieldGuid));
            }

            dispatch(actions.setLastClickedDimIdx(activeTab, itemDimIdx));
        },
        [activeTab, dispatch, field, isSelected, itemDimIdx, store]
    );

    const acres = formatMessage(messages.acres, {
        acres: formatNumber(field.acres, intlConfig.numberFormatOptions),
    });

    const fieldInfo = !field.farmName
        ? acres
        : `${formatMessage(messages.farm, {
              farmName: field.farmName,
          })}, ${acres}`;

    const recText = formatMessage(messages.recText, {
        count: field.recCount,
    });
    const eventText = formatMessage(messages.eventText, {
        count: field.eventCount,
    });

    const boundaryModifiedTitle = !field.boundaryLastModifiedUser
        ? undefined
        : formatMessage(messages.boundaryLastModified, {
              user: field.boundaryLastModifiedUser,
              date: field.boundaryLastModifiedDate,
          });

    return (
        <div
            className={classnames("field-accordion-item", {
                selected: isSelected,
            })}
            onClick={_onClick}
            data-testid={`customers-accordion-field-item-${toTestIdSegment(field.name)}`}
        >
            <div className="field-selected-column">
                <div className="field-selected-mark" />
            </div>
            <div className="field-info" title={`${field.name}\n${fieldInfo}`}>
                <div className="field-name clip-overflow">{field.name}</div>
                <div className="field-farm-acres clip-overflow">
                    {field.farmName
                        ? [
                              <div key="farm" className="clip-overflow">
                                  {formatMessage(messages.farm, {
                                      farmName: field.farmName,
                                  })}
                              </div>,
                              <div key="acres" title={boundaryModifiedTitle}>{`, ${acres}`}</div>,
                              field.lockBoundary ? (
                                  <LockIcon key="lock" className="lock-icon" />
                              ) : null,
                          ]
                        : [
                              <div
                                  key="acres"
                                  className="clip-overflow"
                                  title={boundaryModifiedTitle}
                              >
                                  {acres}
                              </div>,
                              field.lockBoundary ? (
                                  <LockIcon key="lock" className="lock-icon" />
                              ) : null,
                          ]}
                </div>
            </div>
            {!userRole.recommendations ? (
                <div className="rec-count" />
            ) : (
                <div className="rec-count" title={`${field.recCount} ${recText}`}>
                    <div className="clip-overflow">{field.recCount}</div>
                    <div className="units">{recText}</div>
                </div>
            )}
            {!userRole.agEvents ? (
                <div className="event-count" />
            ) : (
                <div className="event-count" title={`${field.eventCount} ${eventText}`}>
                    <div className="clip-overflow">{field.eventCount}</div>
                    <div className="units">{eventText}</div>
                </div>
            )}
            <FieldContextMenu field={field} customer={customer} onAddEditField={onAddEditField} />
        </div>
    );
}, fieldItemPropsAreEqual);
